import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { orderHasBookingProtection } from './orderInsuranceUtils'
import { getMaxOccupancies, getOccupanciesFromReservations } from 'lib/offer/occupancyUtils'
import { isFlashHotelOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import { orderItemHasReservationChannelManager } from './orderItemUtils'

/**
 * Availability for a stay the guest already holds excludes their own room, because their
 * reservation counts against inventory. svc-reservation only adds that room back when the
 * existing reservation id is supplied (the `+1` allowance added in OPEX-2284), so without it
 * a sold-out room type returns no rate for the very package they own — and `canAddExtraGuests`
 * fails its `availableRates` lookup. Extend-dates wired this up in CSX-2284; the extra guest
 * flows did not.
 *
 * Only self-managed inventory is served from svc-reservation's own tables. External channel
 * managers answer availability from the connection, where the id has no effect, so it is
 * left off for them.
 */
export function getExtraGuestsAvailabilityReservationId(item: App.OrderItem | undefined): string | undefined {
  return orderItemHasReservationChannelManager(item, 'self-managed') ? item?.reservation?.id : undefined
}

export function getExtraGuestCapacity(offer: App.HotelOffer, item: App.OrderItem, packageKey: string): {
  adults: number
  children: number
  infants: number
  teenagers: number
} | null {
  if (!offer) return null
  if (!item?.reservation) return null

  const pkg = offer.packages.find(p => p.uniqueKey === packageKey)

  const occupancy = getOccupanciesFromReservations(item.reservation)

  return getMaxOccupancies(pkg?.roomRate.capacities, occupancy, {
    maxChildAge: offer.property?.maxChildAge,
    maxInfantAge: offer.property?.maxInfantAge,
    maxTeenagerAge: offer.property?.maxTeenagerAge,
  })
}

interface canAddExtraGuestsOptions {
  ignoreAvailableRates?: boolean
  canRefund?: boolean
}

export function canAddExtraGuests(offer: App.HotelOffer, order: App.Order, item: App.OrderItem, availableRates: Array<App.OfferAvailableRate>, options: canAddExtraGuestsOptions = {}): boolean {
  if (!offer) return false
  if (!item) return false
  if (!item.reservation) return false
  if (item.status === 'cancelled') return false

  if (!isFlashHotelOffer(offer) && !isLpcHotelOffer(offer)) {
    return false
  }

  if (isLpcHotelOffer(offer) && !options.canRefund) return false
  if (item.reservation?.channelManager !== 'self-managed' && !options.canRefund) return false

  const hasBookingProtection = orderHasBookingProtection(order)
  if (hasBookingProtection) return false

  const hasTransfers = order.transferItems.filter(transfer => transfer.status === 'completed').length > 0
  if (hasTransfers) return false

  const itemPackage = item.package as App.OrderItemHotelPackage
  const key = getPackageUniqueKey(itemPackage.id, itemPackage.duration, itemPackage.roomRateId)

  const maxCapacities = getExtraGuestCapacity(offer, item, key)
  if (!maxCapacities) return false

  const currentRateAvailability = availableRates?.find(rate => rate.packageUniqueKey === key)

  if (!options.ignoreAvailableRates && !currentRateAvailability) return false

  return maxCapacities.adults > item.reservation.adults ||
    maxCapacities.teenagers > (item.reservation.teenagers ?? 0) ||
    maxCapacities.children > (item.reservation.children ?? 0) ||
    maxCapacities.infants > (item.reservation.infants ?? 0)
}
